import { api, supabase } from '@/lib/api/client'

/**
 * Gets the Turnstile token from the widget
 * The Turnstile widget with implicit rendering creates a hidden input with name "cf-turnstile-response"
 * @returns The Turnstile token or null if not available
 */
function getTurnstileToken(): string | null {
  // Try to get token from the hidden input field created by Turnstile
  const input = document.querySelector<HTMLInputElement>(
    'input[name="cf-turnstile-response"]'
  )
  const token = input?.value?.trim()

  if (token && token.length > 0) {
    return token
  }

  // Fallback: try to get from Turnstile widget directly using the API
  // @ts-expect-error - Turnstile global may not be typed
  if (typeof window !== 'undefined' && window.turnstile) {
    try {
      const widgetElement = document.querySelector('.cf-turnstile')
      const widgetId = widgetElement?.getAttribute('data-widget-id')

      if (widgetId) {
        // @ts-expect-error - Turnstile global may not be typed
        const response = window.turnstile.getResponse(widgetId)
        if (response && typeof response === 'string' && response.length > 0) {
          return response
        }
      } else {
        // Try to find widget ID from all Turnstile widgets
        const widgets = document.querySelectorAll('.cf-turnstile')
        for (const widget of widgets) {
          const id = widget.getAttribute('data-widget-id')
          if (id) {
            // @ts-expect-error - Turnstile global may not be typed
            const response = window.turnstile.getResponse(id)
            if (
              response &&
              typeof response === 'string' &&
              response.length > 0
            ) {
              return response
            }
          }
        }
      }
    } catch (e) {
      console.error('Error getting Turnstile token:', e)
    }
  } else {
    console.warn('Turnstile global not available')
  }

  console.warn('No Turnstile token found')
  return null
}

export async function authenticate(
  formData: FormData,
  mode: 'login' | 'signup'
): Promise<{ error?: string }> {
  const email = formData.get('email') as string
  const password = formData.get('password') as string
  const turnstileToken = getTurnstileToken()

  try {
    if (mode === 'login') {
      const requestBody: any = {
        email,
        password,
      }
      if (turnstileToken) {
        requestBody.turnstileToken = turnstileToken
      }

      const response = await api.auth['sign-in'].post(requestBody)

      if (response.error) {
        return { error: 'Failed to sign in' }
      }

      if (response.data && 'error' in response.data) {
        return { error: response.data.error as string }
      }

      // Store session using Supabase SDK
      if (
        response.data &&
        'session' in response.data &&
        'user' in response.data
      ) {
        const session = response.data.session as {
          access_token: string
          refresh_token: string
          expires_at?: number
        }

        console.log('[AUTH] Setting session for user:', response.data.user)
        console.log('[AUTH] Session from backend:', {
          access_token_length: session.access_token?.length,
          refresh_token_length: session.refresh_token?.length,
          expires_at: session.expires_at,
          access_token_start: session.access_token?.substring(0, 20),
        })

        const setSessionResult = await supabase.auth.setSession({
          access_token: session.access_token,
          refresh_token: session.refresh_token,
        })

        console.log('[AUTH] setSession complete:', {
          error: setSessionResult.error,
          hasData: !!setSessionResult.data,
          hasUser: !!setSessionResult.data?.user,
          hasSession: !!setSessionResult.data?.session,
          userId: setSessionResult.data?.user?.id,
        })

        if (setSessionResult.error) {
          console.error('[AUTH] Error setting session:', setSessionResult.error)
          return { error: 'Failed to store session' }
        }

        // Verify it was stored
        const { data: verifyData } = await supabase.auth.getSession()
        console.log('[AUTH] Verification - session in storage:', {
          hasSession: !!verifyData?.session,
          accessToken: verifyData?.session?.access_token?.substring(0, 20),
        })

        // Check what's actually in localStorage
        console.log('[AUTH] LocalStorage keys:', Object.keys(localStorage))
        const allStorage: Record<string, any> = {}
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i)
          if (key) {
            allStorage[key] =
              localStorage.getItem(key)?.substring(0, 50) + '...'
          }
        }
        console.log('[AUTH] LocalStorage contents:', allStorage)

        console.log('[AUTH] Session set successfully')
      } else {
        console.warn('[AUTH] No session in response data:', response.data)
      }
    } else {
      const requestBody: any = {
        email,
        password,
      }
      if (turnstileToken) {
        requestBody.turnstileToken = turnstileToken
      }

      const response = await api.auth['sign-up'].post(requestBody)

      if (response.error) {
        return { error: 'Failed to sign up' }
      }

      if (response.data && 'error' in response.data) {
        return { error: response.data.error as string }
      }

      // Store session using Supabase SDK (if session exists - email confirmation may be required)
      if (
        response.data &&
        'session' in response.data &&
        'user' in response.data
      ) {
        const session = response.data.session as {
          access_token: string
          refresh_token: string
          expires_at?: number
        }

        // Store session using Supabase SDK for new user
        const { data: setSessionData, error: setSessionError } =
          await supabase.auth.setSession({
            access_token: session.access_token,
            refresh_token: session.refresh_token,
          })

        if (setSessionError) {
          console.error('[AUTH] Error setting session:', setSessionError)
          return { error: 'Failed to store session' }
        }

        console.log('[AUTH] Session set successfully:', setSessionData)
      } else {
        console.log(
          '[AUTH] No session in signup response (email confirmation may be required)'
        )
      }
    }

    return {}
  } catch (error) {
    console.error('Authentication error:', error)
    return { error: 'Authentication failed' }
  }
}

export async function signOut(): Promise<{ error?: string }> {
  try {
    // First, call the backend sign-out endpoint while we still have auth
    const response = await api.auth['sign-out'].post()

    if (response.error) {
      return { error: 'Failed to sign out' }
    }

    // Clear the Supabase session
    await supabase.auth.signOut()

    // Immediately reload the page to prevent components from making requests without auth
    if (typeof window !== 'undefined') {
      window.location.href = '/auth'
    }

    return {}
  } catch (error) {
    console.error('Sign out error:', error)
    return { error: 'Sign out failed' }
  }
}

export async function requestPasswordReset(
  formData: FormData
): Promise<{ error?: string; message?: string }> {
  const email = formData.get('email') as string

  try {
    const response = await api.auth['forgot-password'].post({ email })

    if (response.error) {
      return { error: 'Failed to send reset email' }
    }

    if (response.data && 'error' in response.data) {
      return { error: response.data.error as string }
    }

    if (response.data && 'message' in response.data) {
      return { message: response.data.message as string }
    }

    return {
      message:
        'If an account exists with this email, a password reset link has been sent.',
    }
  } catch (error) {
    console.error('Password reset request error:', error)
    return { error: 'Failed to send reset email. Please try again.' }
  }
}

export async function verifyResetToken(
  tokenHash: string,
  type: string
): Promise<{ error?: string; session?: any; user?: any }> {
  try {
    console.log('[VERIFY RESET TOKEN] Starting verification')
    console.log(
      '[VERIFY RESET TOKEN] Token hash:',
      tokenHash?.substring(0, 20) + '...'
    )
    console.log('[VERIFY RESET TOKEN] Type:', type)

    const response = await api.auth['verify-reset-token'].post({
      token_hash: tokenHash,
      type,
    })

    console.log('[VERIFY RESET TOKEN] Response received:', {
      hasError: !!response.error,
      hasData: !!response.data,
      dataKeys: response.data ? Object.keys(response.data) : [],
    })

    if (response.error) {
      console.error('[VERIFY RESET TOKEN] API error:', response.error)
      return { error: 'Failed to verify reset token' }
    }

    if (response.data && 'error' in response.data) {
      console.error('[VERIFY RESET TOKEN] Backend error:', response.data.error)
      return { error: response.data.error as string }
    }

    if (
      response.data &&
      'session' in response.data &&
      'user' in response.data
    ) {
      const session = response.data.session as {
        access_token: string
        refresh_token: string
        expires_at?: number
      }
      const user = response.data.user

      console.log('[VERIFY RESET TOKEN] Session received from backend:', {
        hasAccessToken: !!session.access_token,
        accessTokenLength: session.access_token?.length,
        hasRefreshToken: !!session.refresh_token,
        expiresAt: session.expires_at,
      })

      // Store session using the singleton Supabase client
      console.log('[VERIFY RESET TOKEN] Calling supabase.auth.setSession()')
      const setSessionResult = await supabase.auth.setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      })

      console.log('[VERIFY RESET TOKEN] setSession result:', {
        hasError: !!setSessionResult.error,
        error: setSessionResult.error,
        hasSession: !!setSessionResult.data?.session,
        hasUser: !!setSessionResult.data?.user,
      })

      // Immediately verify it was stored
      const { data: verifyData } = await supabase.auth.getSession()
      console.log('[VERIFY RESET TOKEN] Verification - session in storage:', {
        hasSession: !!verifyData?.session,
        hasAccessToken: !!verifyData?.session?.access_token,
        accessTokenStart: verifyData?.session?.access_token?.substring(0, 20),
      })

      return { session, user }
    }

    console.error('[VERIFY RESET TOKEN] Invalid response structure')
    return { error: 'Invalid response from server' }
  } catch (error) {
    console.error('[VERIFY RESET TOKEN] Exception:', error)
    return { error: 'Failed to verify reset token. Please try again.' }
  }
}

export async function resetPassword(
  password: string
): Promise<{ error?: string; message?: string }> {
  try {
    console.log('[RESET PASSWORD] Starting password reset')

    // Check if we have a session BEFORE making the request
    const {
      data: { session: currentSession },
    } = await supabase.auth.getSession()
    console.log('[RESET PASSWORD] Current session check:', {
      hasSession: !!currentSession,
      hasAccessToken: !!currentSession?.access_token,
      accessTokenStart: currentSession?.access_token?.substring(0, 20),
    })

    if (!currentSession?.access_token) {
      return {
        error: 'No active session. Please request a new password reset link.',
      }
    }

    console.log('[RESET PASSWORD] Making request via Eden Treaty client')
    const response = await api.auth['reset-password'].post({ password })

    console.log('[RESET PASSWORD] Response received:', {
      hasError: !!response.error,
      hasData: !!response.data,
    })

    if (response.error) {
      console.error('[RESET PASSWORD] API error:', response.error)
      return { error: 'Failed to reset password' }
    }

    if (response.data && 'error' in response.data) {
      console.error('[RESET PASSWORD] Backend error:', response.data.error)
      return { error: response.data.error as string }
    }

    if (response.data && 'message' in response.data) {
      console.log('[RESET PASSWORD] Success:', response.data.message)
      return { message: response.data.message as string }
    }

    return { message: 'Password reset successfully' }
  } catch (error) {
    console.error('[RESET PASSWORD] Exception:', error)
    return { error: 'Failed to reset password. Please try again.' }
  }
}

export async function verifyEmail(
  tokenHash: string,
  type: string
): Promise<{ error?: string; session?: any; user?: any }> {
  try {
    const response = await api.auth['verify-email'].post({
      token_hash: tokenHash,
      type,
    })

    if (response.error) {
      return { error: 'Failed to verify email' }
    }

    if (response.data && 'error' in response.data) {
      return { error: response.data.error as string }
    }

    if (
      response.data &&
      'session' in response.data &&
      'user' in response.data
    ) {
      const session = response.data.session as {
        access_token: string
        refresh_token: string
        expires_at?: number
      }
      const user = response.data.user

      // Store session using the singleton Supabase client
      const { error } = await supabase.auth.setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      })

      if (error) {
        console.error('Failed to store session:', error)
        return { error: 'Failed to store session' }
      }

      return { session, user }
    }

    return { error: 'Invalid response from server' }
  } catch (error) {
    console.error('Email verification error:', error)
    return { error: 'Failed to verify email. Please try again.' }
  }
}

export async function verifyInvite(
  tokenHash: string,
  type: string
): Promise<{ error?: string; session?: any; user?: any }> {
  try {
    const response = await api.auth['verify-invite'].post({
      token_hash: tokenHash,
      type,
    })

    if (response.error) {
      return { error: 'Failed to verify invitation' }
    }

    if (response.data && 'error' in response.data) {
      return { error: response.data.error as string }
    }

    if (
      response.data &&
      'session' in response.data &&
      'user' in response.data
    ) {
      const session = response.data.session as {
        access_token: string
        refresh_token: string
        expires_at?: number
      }
      const user = response.data.user

      // Store session using the singleton Supabase client
      const { error } = await supabase.auth.setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      })

      if (error) {
        console.error('Failed to store session:', error)
        return { error: 'Failed to store session' }
      }

      return { session, user }
    }

    return { error: 'Invalid response from server' }
  } catch (error) {
    console.error('Invite verification error:', error)
    return { error: 'Failed to verify invitation. Please try again.' }
  }
}
